import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import {  forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CasesService } from 'src/app/services/cases.service';
import { IFileWithValidation } from 'src/app/services/file-upload.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { ICaseMessage, IInvoice } from 'src/app/shared/models';
import { InvoiceListComponent } from '../invoices/invoice-list.component';
import { FileUploadComponent } from 'src/app/shared/file-upload/file-upload.component';

@Component({
  selector: 'app-case-invoices',
  templateUrl: './case-invoices.component.html',
  styleUrls: ['./case-invoices.component.scss'],
  imports: [CommonModule, TranslateModule, MatDialogModule, CustomSelectComponent, InvoiceListComponent, FileUploadComponent ]
})
export class CaseInvoicesComponent {

  #changeDetector = inject(ChangeDetectorRef)

  constructor(private service: CasesService, private spinner: GlobalSpinnerService, private notification: MessageToUiService
    , public matDialog: MatDialog, private translate: TranslateService) { }

  @Input() caseNumber: number;
  @Input() list: IInvoice[];
  @Input() caseNotes: ICaseMessage[] = [];
  @Input() hasInternationalCurrency: boolean;
  @Input() caseCurrencyCode: string;
  @Input() firmCurrencyCode: string;
  @Input() numberFormat: LocalizedNumberFormat;
  @Output() invoiceUploaded = new EventEmitter();
  @Output() change = new EventEmitter();

  public get uploadEnabled(): boolean {
    return this.list?.filter(x => x.isInvoiceCopyAvailable).length != this.list?.length;
  }

  uploadDialogRef: MatDialogRef<any, any>;
  fileNames: KeyValue<number, string>[];
  fileSelectionError: boolean = false;


  openInvoiceCopy(invoice: IInvoice): void {
    if (!this.caseNumber || !invoice?.isInvoiceCopyAvailable) { return; }

    this.spinner.show();

    this.service.getInvoiceCopy(this.caseNumber, invoice.number)
      .subscribe({
        next: (blob) => {
          this.spinner.hide();

          const fileURL = window.URL.createObjectURL(blob);
          const tab = window.open();
          tab.location.href = fileURL;
        }
        , error: () => {
          this.spinner.hide();
          this.notification.errorKey('Errors.FileNotFound');
        }
      });
  }

  downloadInvoices() {
    this.spinner.show();
    this.service.downloadInvoicesExcel(this.caseNumber, this.translate.getDefaultLang())
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (blob) => {
          saveAs(blob, `${this.translate.instant('Case.Invoices')}_${this.caseNumber}.xlsx`);
        },
        error: () => {
          this.notification.genericError();
        }
      });
  }

  handleSelectedFiles(component, event) {
    const files: File[] = event ?? [];

    const invoices = this.list
      .filter(i => !i.isInvoiceCopyAvailable)
      .map(invoice => Object.assign({ fileIndex: null, status: 'none' }, invoice))

    // auto select file
    if (invoices?.length == 1 && files.length == 1) {
      invoices[0].fileIndex = 0;
    }

    this.fileNames = files.map((x,i) => ({ key: i++, value: x.name } as KeyValue<number, string>)); 

    this.uploadDialogRef = this.matDialog.open(component, { data: { invoices, files: files } } )
    this.uploadDialogRef.afterClosed().subscribe(_ => this.change.next(null))
  }

  upload(invoices: Array<InvoiceWithStatus>, files: IFileWithValidation[]): void {
    
    this.spinner.show();

    const uploadRequestArray = this.buildUploadRequestArray(invoices, files);
    if (!uploadRequestArray.length) {

      this.spinner.hide();
      this.uploadDialogRef.close();
    }

    forkJoin(uploadRequestArray).subscribe(
      results => {

        this.spinner.hide();

        const allUploadsSucceed = results.filter(succcess => succcess).length === results.length;

        if (allUploadsSucceed) {
          this.invoiceUploaded.next(null);
          this.uploadDialogRef.close();
        }
      });
  }

  private buildUploadRequestArray(invoices: Array<InvoiceWithStatus>, files: Array<IFileWithValidation>) {
    const notifyDebtor = this.caseNotes.filter(x => x.categoryCode == 'BRFKOPB').length > 0;
    return invoices
      .filter(i => (i.fileIndex >= 0) && (i.status === 'none'))
      .map(i => {
        i.status = 'busy';

        return this.service.uploadInvoiceCopy(this.caseNumber, i.number, files[i.fileIndex], notifyDebtor)
          .pipe(
            map(_ => { i.status = 'success'; return true; }),
            catchError(_ => { i.status = 'error'; return of(false); }));
      });
  }
}

export type InvoiceWithStatus = IInvoice & { fileIndex?: number, status: 'none' | 'busy' | 'success' | 'error' }
